import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { EagleLogo } from "@/components/EagleLogo";
import { Printer, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import LoadError from "@/components/LoadError";

export default function SafetyComplianceRollup() {
    const location = useLocation();
    const rolePrefix = location.pathname.startsWith("/foreman") ? "/foreman" : "/admin";
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = async () => {
        setLoading(true);
        setError(false);
        try {
            const params = new URLSearchParams();
            if (start) params.set("start", start);
            if (end) params.set("end", end);
            const r = await api.get(`/safety/compliance-report${params.toString() ? `?${params}` : ""}`);
            setData(r.data);
        } catch (e) {
            setData(null);
            setError(true);
            toast.error(e?.response?.data?.detail || "Failed to load compliance report");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (!start && !end) load(); }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps
    const clearRange = () => { setStart(""); setEnd(""); };

    return (
        <div className="paper-bg pb-28 min-h-screen" data-testid="safety-compliance-rollup">
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-3 no-print">
                    {data && (
                        <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-brand-admin-header hover:bg-[#6E1824] text-brand-bone font-bold text-sm" data-testid="print-report-button">
                            <Printer className="h-4 w-4" /> Print / Save as PDF
                        </button>
                    )}
                </div>

                <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-6 print:shadow-none print:border-black">
                    <div className="flex items-center gap-3 pb-4 border-b border-brand-charcoal/15">
                        <EagleLogo className="h-10 w-10" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-charcoal/50">Talon Construction Company — Safety Compliance Record</div>
                            <div className="font-bold text-xl flex items-center gap-2"><ClipboardList className="h-5 w-5 text-brand-navy" /> Compliance Report</div>
                            <div className="text-sm text-brand-charcoal/60 mt-0.5">
                                {start || end ? `${start ? dayjs(start).format("MMM D, YYYY") : "the beginning"} – ${end ? dayjs(end).format("MMM D, YYYY") : "now"}` : "All-time"}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-end gap-3 mt-4 no-print">
                        <div>
                            <label className="block text-[10px] uppercase tracking-widest text-brand-admin-header mb-1">Start date</label>
                            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal" data-testid="rollup-start-date" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-widest text-brand-admin-header mb-1">End date</label>
                            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal" data-testid="rollup-end-date" />
                        </div>
                        <button onClick={load} className="h-11 px-5 rounded-xl bg-brand-admin-header hover:bg-[#6E1824] text-brand-bone font-bold" data-testid="run-rollup-button">Apply</button>
                        {(start || end) && (
                            <button onClick={clearRange} className="h-11 px-4 rounded-xl bg-brand-stone text-brand-charcoal font-bold" data-testid="clear-rollup-range-button">All-time</button>
                        )}
                    </div>

                    {loading && !data && <div className="text-brand-charcoal/60 text-center py-8">Loading…</div>}

                    {!loading && error && (
                        <div className="mt-4">
                            <LoadError
                                message="Couldn't load the compliance report — this is not the same as no records existing."
                                onRetry={load}
                                testId="rollup-load-error"
                            />
                        </div>
                    )}

                    {data && (
                        <>
                            <div className="mt-6">
                                <div className="text-[10px] uppercase tracking-widest text-brand-admin-header font-bold mb-2">
                                    Meetings in range ({data.meeting_count})
                                </div>
                                {data.meetings.length === 0 ? (
                                    <p className="text-sm text-brand-charcoal/50" data-testid="no-meetings-in-range">No safety meetings posted in this range.</p>
                                ) : (
                                    <table className="w-full text-sm" data-testid="rollup-meetings-table">
                                        <thead>
                                            <tr className="bg-brand-stone text-left text-[10px] uppercase tracking-widest text-brand-charcoal/70">
                                                <th className="px-3 py-2">Title</th>
                                                <th className="px-3 py-2">Posted</th>
                                                <th className="px-3 py-2">Source</th>
                                                <th className="px-3 py-2 text-right">Watched</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.meetings.map((m) => (
                                                <tr key={m.meeting_id} className="border-t border-brand-charcoal/10">
                                                    <td className="px-3 py-2 font-semibold text-brand-charcoal">
                                                        <Link to={`${rolePrefix}/safety/${m.meeting_id}/report`} className="hover:underline no-print">{m.title}</Link>
                                                        <span className="hidden print:inline">{m.title}</span>
                                                    </td>
                                                    <td className="px-3 py-2 text-brand-charcoal/60">{dayjs(m.created_at).format("MMM D, YYYY")}</td>
                                                    <td className="px-3 py-2 text-brand-charcoal/60 max-w-[200px] truncate">{m.source_note || (m.source_url ? m.source_url : "—")}</td>
                                                    <td className="px-3 py-2 text-right font-bold text-brand-navy">{m.watched_count}/{m.total_audience}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="mt-6">
                                <div className="text-[10px] uppercase tracking-widest text-brand-admin-header font-bold mb-2">
                                    Employee compliance
                                </div>
                                {data.employees.length === 0 ? (
                                    <p className="text-sm text-brand-charcoal/50">No crew on record.</p>
                                ) : (
                                    <table className="w-full text-sm" data-testid="rollup-employees-table">
                                        <thead>
                                            <tr className="bg-brand-stone text-left text-[10px] uppercase tracking-widest text-brand-charcoal/70">
                                                <th className="px-3 py-2">Employee</th>
                                                <th className="px-3 py-2">Code</th>
                                                <th className="px-3 py-2 text-right">Completed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.employees.map((e) => {
                                                const complete = e.required > 0 && e.completed >= e.required;
                                                return (
                                                    <tr key={e.employee_id} className="border-t border-brand-charcoal/10">
                                                        <td className="px-3 py-2 font-semibold text-brand-charcoal">{e.name}</td>
                                                        <td className="px-3 py-2 text-brand-charcoal/60">{e.employee_code}</td>
                                                        <td className={`px-3 py-2 text-right font-bold ${complete ? "text-brand-success" : "text-brand-danger"}`}>
                                                            {e.completed} / {e.required}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    )}

                    <p className="mt-6 pt-4 border-t border-brand-charcoal/10 text-[10px] text-brand-charcoal/40">
                        Generated {dayjs().format("MMMM D, YYYY, h:mm A")} · The Nest — Talon Construction Company
                    </p>
                </div>
            </div>
        </div>
    );
}
